import moment from 'moment';
import {
    RPM_ALERT_SEVERITIES,
    RPM_ALERT_TYPE_CRITICAL,
    RPM_ALERT_TYPE_RESPONSE,
    RPM_ALERT_TYPE_WARNING,
    isRpmThresholdAlertType,
} from '../constants/other';

const getRowAlerts = (row) => (row && row.alerts) || [];

const pickAlertField = (alert, keys) => {
    for (let i = 0; i < keys.length; i += 1) {
        const value = alert[keys[i]];
        if (value != null && value !== '') {
            return value;
        }
    }
    return null;
};

/**
 * API to-do timestamps are `yyyy-MM-dd HH:mm` UTC without a zone marker.
 */
export const parseAlertTs = (value) => {
    if (!value) {
        return null;
    }
    const parsed = moment.utc(value).local();
    return parsed.isValid() ? parsed : null;
};

const parseAlertTsUtc = (value) => {
    if (!value) {
        return null;
    }
    const parsed = moment.utc(value);
    return parsed.isValid() ? parsed : null;
};

/** Client due SLA from create_ts by severity (APP-3772). */
export const computeAlertDueTs = (alert) => {
    const createRaw = alert.create_ts || alert.received_ts;
    const created = parseAlertTsUtc(createRaw);
    if (!created) {
        return alert.due_date || alert.due_ts || null;
    }
    const typeId = Number(alert.urgent_todo_type_id);
    let addMinutes = 60;
    if (typeId === RPM_ALERT_TYPE_CRITICAL) {
        addMinutes = 15;
    } else if (typeId === RPM_ALERT_TYPE_WARNING) {
        addMinutes = 60;
    } else if (typeId === RPM_ALERT_TYPE_RESPONSE) {
        addMinutes = 90;
    }
    return created.clone().add(addMinutes, 'minutes').format('YYYY-MM-DD HH:mm');
};

export const formatAlertTs = (value) => {
    const parsed = parseAlertTs(value);
    return parsed ? parsed.format('MMM D, YYYY, h:mm a') : '';
};

export const isAlertOverdue = (dueTs) => {
    const due = parseAlertTs(dueTs);
    return !!due && moment().isAfter(due);
};

export const dueRelativeLabel = (dueTs) => {
    const due = parseAlertTs(dueTs);
    if (!due) {
        return '';
    }
    if (moment().isAfter(due)) {
        return overdueLabel(dueTs);
    }
    const minutes = due.diff(moment(), 'minutes');
    if (minutes < 60) {
        return `in ${minutes} ${minutes === 1 ? 'min' : 'mins'}`;
    }
    const hours = due.diff(moment(), 'hours');
    if (hours < 24) {
        return `in ${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
    }
    const days = due.diff(moment(), 'days');
    return `in ${days} ${days === 1 ? 'day' : 'days'}`;
};

export const overdueLabel = (dueTs) => {
    const due = parseAlertTs(dueTs);
    if (!due) {
        return '';
    }
    const minutes = moment().diff(due, 'minutes');
    if (minutes < 1) {
        return 'just past due';
    }
    if (minutes < 60) {
        return `${minutes} ${minutes === 1 ? 'min' : 'mins'} overdue`;
    }
    const hours = moment().diff(due, 'hours');
    if (hours < 24) {
        return `${hours} ${hours === 1 ? 'hr' : 'hrs'} overdue`;
    }
    const days = moment().diff(due, 'days');
    return `${days} ${days === 1 ? 'day' : 'days'} overdue`;
};

export const getPatientAge = (row) => {
    if (row.age != null) {
        return row.age;
    }
    const dob = row.date_of_birth ? moment(row.date_of_birth, 'YYYY-MM-DD') : null;
    return dob && dob.isValid() ? moment().diff(dob, 'years') : null;
};

export const formatDob = (dateOfBirth) => {
    if (!dateOfBirth) {
        return '';
    }
    const dob = moment(dateOfBirth, 'YYYY-MM-DD');
    return dob.isValid() ? dob.format('MM/DD/YYYY') : '';
};

const alertMatchesSeverity = (alert, severityTypeId) => {
    if (!severityTypeId) {
        return true;
    }
    return alert.urgent_todo_type_id == severityTypeId;
};

const enrichAlert = (group, alert) => {
    const createTs = alert.create_ts || alert.received_ts;
    const dueDateTs = computeAlertDueTs({ ...alert, create_ts: createTs });
    return {
        ...alert,
        create_ts: createTs,
        due_date_ts: dueDateTs,
        due_date: dueDateTs,
        rpm_alert_type_name: pickAlertField(alert, [
            'urgent_todo_type_name',
            'alert_type_name',
            'alert_type',
            'type_name',
            'metric_name',
            'name',
            'title',
        ]) || pickAlertField(group, ['alert_type_name', 'alert_type', 'metric_name']),
        rpm_threshold_name: pickAlertField(alert, [
            'threshold_name',
            'threshold_label',
            'threshold_type',
            'threshold',
            'threshold_description',
        ]) || pickAlertField(group, ['threshold_name', 'threshold_label', 'threshold_type']),
    };
};

const compareDueTs = (a, b) => {
    const dueA = parseAlertTsUtc(a.due_date_ts);
    const dueB = parseAlertTsUtc(b.due_date_ts);
    if (!dueA && !dueB) {
        return 0;
    }
    if (!dueA) {
        return 1;
    }
    if (!dueB) {
        return -1;
    }
    return dueA.valueOf() - dueB.valueOf();
};

/** Header count when severity is filtered client-side (full alert list per row). */
export const resolveRpmAlertQueueTotalItems = (
    severityTypeId,
    totalItems,
    severityCounts,
    filteredRowCount
) => {
    if (!severityTypeId) {
        return totalItems;
    }
    const severity = RPM_ALERT_SEVERITIES.find((entry) => entry.typeId == severityTypeId);
    if (severity && severityCounts && severityCounts[severity.key] != null) {
        return severityCounts[severity.key];
    }
    return filteredRowCount;
};

/**
 * One queue row per patient + device group. Alerts get client due_date_ts, sorted ascending;
 * earliest due drives the Due Date & Time column.
 *
 * Severity filter: include the row only if the group has at least one matching alert, but always
 * show every alert on that row (not a severity-scoped subset).
 */
export const prepareRpmAlertQueueItems = (items, severityTypeId = null) => {
    if (!items || !items.length) {
        return [];
    }
    const rows = [];
    items.forEach((group) => {
        const allAlerts = getRowAlerts(group);
        if (!allAlerts.length) {
            return;
        }

        const hasMatchingSeverity = !severityTypeId
            || allAlerts.some((alert) => alertMatchesSeverity(alert, severityTypeId));
        if (!hasMatchingSeverity) {
            return;
        }

        const sortedAlerts = allAlerts
            .map((alert) => enrichAlert(group, alert))
            .sort(compareDueTs);

        rows.push({
            ...group,
            id: group.patient_id != null ? group.patient_id : group.id,
            alerts: sortedAlerts,
            rpm_sorted_alerts: sortedAlerts,
            rpm_alert_total: sortedAlerts.length,
            rpm_earliest_due_ts: sortedAlerts[0] ? sortedAlerts[0].due_date_ts : null,
        });
    });
    return rows;
};

/** Open RPM threshold alerts on the patient chart (urgent_todo_type_id 27, 28, 29). */
export const resolveRpmThresholdChartAlerts = (todoLists) => {
    const openTodos = Object.values(todoLists || {})
        .filter((todo) => isRpmThresholdAlertType(todo.urgent_todo_type_id));

    return openTodos
        .map((todo) => enrichAlert({}, {
            ...todo,
            todo_id: todo.id,
            id: todo.id,
            urgent: todo.urgent,
            urgent_todo_type_id: todo.urgent_todo_type_id,
            description: todo.description,
            create_ts: todo.create_ts || todo.urgent_todo_create_ts,
        }))
        .sort(compareDueTs);
};
